import React from 'react';
import { Link } from 'react-router-dom';
import { colors, semanticColors } from '../../theme';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useScrollToTop } from '../../hooks/useScrollToTop';

const TermsOfService: React.FC = () => {
  // Scroll to top when component mounts
  useScrollToTop('smooth');

  return (
    <div className="min-h-screen" style={{ backgroundColor: semanticColors.background.primary }}>
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/"
            className="inline-flex items-center text-sm hover:underline transition-colors duration-200 mb-4"
            style={{ color: colors.primary }}
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ color: colors.primary }}
          >
            Vital Hive Technologies Terms of Service
          </h1>
          
          <p className="text-sm" style={{ color: semanticColors.text.secondary }}>
            Effective Date: August 05, 2025
          </p>
        </div>

        {/* Preamble */}
        <div className="mb-8 p-6 rounded-lg" style={{ backgroundColor: semanticColors.background.secondary, border: `1px solid ${semanticColors.border.light}` }}>
          <p className="text-sm leading-relaxed" style={{ color: semanticColors.text.secondary }}>
            These Terms of Service (the "Terms") govern the use of the Blue Water Healthcare platform by healthcare facilities seeking to connect with independent healthcare professionals ("Providers") for shift-based engagements. By accessing or using the Blue Water Healthcare platform (the "Platform") or Services, you accept these Terms on behalf of yourself and any Facility or entity you represent.
          </p>
        </div>

        {/* Section 1: How This Agreement Works */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            1. HOW THIS AGREEMENT WORKS
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              1.1 Binding Effect
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              These Terms govern your use of the Platform and are incorporated by reference into any executed agreement. If no agreement has been signed, continued access to the Platform constitutes your binding agreement to these Terms.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              1.2 Modifications
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water Healthcare ("Blue Water," "we," "our," or "us") may revise these Terms at any time by posting updated terms to the Platform. We will provide reasonable notice (email, in-app notice, etc.) of material changes. Your continued use of the Platform after changes take effect constitutes acceptance. Shifts already booked prior to changes will remain subject to the previous Terms, unless compliance with updated Terms is mandated by law.
            </p>
          </div>
        </section>

        {/* Section 2: Account Setup & Platform Access */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            2. ACCOUNT SETUP & PLATFORM ACCESS
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              2.1 Facility Account Registration
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Facilities must register an account and designate at least one Authorized User. You represent that you have the authority to bind your Facility to these Terms. You are responsible for all actions taken under your Facility's account, including those of other Authorized Users.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              2.2 Credentialed Access
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water may require identity and organizational verification before granting full access. We may suspend or terminate access for incomplete, inaccurate, or misleading account information.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              2.3 Authorized Use
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              You agree to use the Platform solely for lawful purposes and internal Facility operations. Prohibited uses include:
            </p>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Misuse of Provider credentials or impersonation</li>
              <li>Scraping, harvesting, or exporting data without consent</li>
              <li>Reverse engineering or circumvention of security controls</li>
              <li>Using the Platform in violation of healthcare laws, labor rules, or privacy obligations</li>
            </ul>
          </div>
        </section>

        {/* Section 3: Platform Functionality & Matching Services */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            3. PLATFORM FUNCTIONALITY & MATCHING SERVICES
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              3.1 Service Overview
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water provides technology tools that facilitate communication and shift-matching between Facilities and independent Providers. Blue Water is not a staffing agency or employer of Providers.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              3.2 No Guarantee
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              We do not guarantee that:
            </p>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Providers will respond to or accept shift requests;</li>
              <li>Providers are available in your area;</li>
              <li>Services will meet your expectations.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              3.3 Provider Credential Verification
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water may perform limited credential checks (license, insurance, basic background), but does not endorse, supervise, or guarantee Provider qualifications or conduct. Facilities are solely responsible for verifying Provider compliance with all applicable laws, including HIPAA, OSHA, and local licensing rules.
            </p>
          </div>
        </section>

        {/* Section 4: Facility Obligations */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            4. FACILITY OBLIGATIONS
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              4.1 Compliance
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Comply with all applicable healthcare laws, labor regulations, and safety standards;</li>
              <li>Maintain current business and malpractice insurance coverage;</li>
              <li>Avoid discriminatory practices in shift postings or Provider engagement;</li>
              <li>Promptly report misconduct or safety issues through the Platform's incident reporting process.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              4.2 Facility-Provider Relationship
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Each shift engagement is a direct independent contractor relationship between you and the Provider. Blue Water is not responsible for Provider performance or any medical or legal liability arising from their services.
            </p>
          </div>
        </section>

        {/* Section 5: Payment & Fees */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            5. PAYMENT & FEES
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              5.1 Payment Processing
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water collects payment for completed shifts on behalf of Providers. Payment must be made within the timeframes specified in your agreement or within 10 business days if no such agreement exists.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              5.2 Platform Fees
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              You agree to pay all applicable Platform fees as defined in your agreement or as disclosed during shift posting. Late payments may incur interest charges or account suspension.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              5.3 No Withholding
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water is not the employer of Providers and does not withhold taxes, provide benefits, or issue W-2s. Facilities may be responsible for reporting compensation to applicable regulatory bodies.
            </p>
          </div>
        </section>

        {/* Section 6: Intellectual Property */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            6. INTELLECTUAL PROPERTY
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              6.1 Platform License
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water grants you a limited, non-transferable, revocable license to access the Platform for business purposes. You may not copy, modify, sell, or sublicense any portion of the Platform without express permission.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              6.2 Content Ownership
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              All content, code, features, and trademarks on the Platform are owned by Blue Water or its licensors. You agree not to use Blue Water branding or content for your own marketing or competitive purposes.
            </p>
          </div>
        </section>

        {/* Section 3: Platform Functionality & Matching Services */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            3. PLATFORM FUNCTIONALITY & MATCHING SERVICES
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              3.1 Service Overview
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water provides technology tools that facilitate communication and shift-matching between Facilities and independent Providers. Blue Water is not a staffing agency or employer of Providers.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              3.2 No Guarantee
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              We do not guarantee that:
            </p>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Providers will respond to or accept shift requests;</li>
              <li>Providers are available in your area;</li>
              <li>Services will meet your expectations.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              3.3 Provider Credential Verification
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water may perform limited credential checks (license, insurance, basic background), but does not endorse, supervise, or guarantee Provider qualifications or conduct. Facilities are solely responsible for verifying Provider compliance with all applicable laws, including HIPAA, OSHA, and local licensing rules.
            </p>
          </div>
        </section>

        {/* Section 4: Facility Obligations */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            4. FACILITY OBLIGATIONS
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              4.1 Compliance
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Comply with all applicable healthcare laws, labor regulations, and safety standards;</li>
              <li>Maintain current business and malpractice insurance coverage;</li>
              <li>Avoid discriminatory practices in shift postings or Provider engagement;</li>
              <li>Promptly report misconduct or safety issues through the Platform's incident reporting process.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              4.2 Facility-Provider Relationship
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Each shift engagement is a direct independent contractor relationship between you and the Provider. Blue Water is not responsible for Provider performance or any medical or legal liability arising from their services.
            </p>
          </div>
        </section>

        {/* Section 5: Payment & Fees */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            5. PAYMENT & FEES
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              5.1 Payment Processing
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water collects payment for completed shifts on behalf of Providers. Payment must be made within the timeframes specified in your agreement or within 10 business days if no such agreement exists.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              5.2 Platform Fees
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              You agree to pay all applicable Platform fees as defined in your agreement or as disclosed during shift posting. Late payments may incur interest charges or account suspension.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              5.3 No Withholding
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water is not the employer of Providers and does not withhold taxes, provide benefits, or issue W-2s. Facilities may be responsible for reporting compensation to applicable regulatory bodies.
            </p>
          </div>
        </section>

        {/* Section 6: Intellectual Property */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            6. INTELLECTUAL PROPERTY
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              6.1 Platform License
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Blue Water grants you a limited, non-transferable, revocable license to access the Platform for business purposes. You may not copy, modify, sell, or sublicense any portion of the Platform without express permission.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              6.2 Content Ownership
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              All content, code, features, and trademarks on the Platform are owned by Blue Water or its licensors. You agree not to use Blue Water branding or content for your own marketing or competitive purposes.
            </p>
          </div>
        </section>

        {/* Section 7: Confidentiality & Privacy */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            7. CONFIDENTIALITY & PRIVACY
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              7.1 Confidential Information
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Any non-public business, patient, or Provider information you access via the Platform is confidential and may only be used for authorized shift coordination purposes. You must report any unauthorized disclosure or breach.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              7.2 Privacy Policy
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              Your use of the Platform is subject to the Blue Water Privacy Policy. You are responsible for complying with all HIPAA and data protection obligations within your Facility.
            </p>
          </div>
        </section>

        {/* Section 8: Disclaimers & Limitations of Liability */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            8. DISCLAIMERS & LIMITATIONS OF LIABILITY
          </h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              8.1 No Warranties
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              The Platform is provided "as-is." Blue Water does not warrant:
            </p>
            <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
              <li>Provider reliability, professionalism, or licensure;</li>
              <li>Uninterrupted or error-free access to the Platform;</li>
              <li>Suitability of the Services for your regulatory obligations.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3" style={{ color: colors.primary }}>
              8.2 Limitation of Liability
            </h3>
            <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
              To the maximum extent permitted by law, Blue Water is not liable for indirect, incidental, or consequential damages, including lost profits, medical malpractice claims, or regulatory penalties. Our liability is limited to the fees paid by you to Blue Water in the six (6) months preceding the incident, giving rise to the claim.
            </p>
          </div>
        </section>

        {/* Section 9: Indemnification */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            9. INDEMNIFICATION
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            You agree to defend and hold harmless Blue Water from claims, losses, and expenses arising from:
          </p>
          <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
            <li>Your misuse of the Platform;</li>
            <li>Your failure to comply with laws or these Terms;</li>
            <li>Disputes between your Facility and any Provider;</li>
            <li>Personal injury, malpractice, or other harms that occur during a Shift.</li>
          </ul>
        </section>

        {/* Section 10: Termination */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            10. TERMINATION
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            Blue Water may suspend or terminate your access:
          </p>
          <ul className="list-disc pl-6 space-y-2 mb-4" style={{ color: semanticColors.text.secondary }}>
            <li>For breach of these Terms;</li>
            <li>For failure to pay fees;</li>
            <li>If required by law or court order.</li>
          </ul>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            You may terminate your account by written notice to Blue Water, subject to outstanding obligations. Upon termination, your account will be deactivated but archived for legal and billing purposes.
          </p>
        </section>

        {/* Section 11: Disputes & Governing Law */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            11. DISPUTES & GOVERNING LAW
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            These Terms are governed by the laws of the State of Ohio, without regard to conflicts of law principles. Any dispute arising out of these Terms shall be resolved in the state or federal courts located in Lucas County, Ohio, and both parties consent to exclusive jurisdiction therein.
          </p>
        </section>

        {/* Section 12: General Terms */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            12. GENERAL TERMS
          </h2>
          <ul className="list-disc pl-6 space-y-2" style={{ color: semanticColors.text.secondary }}>
            <li><strong>Entire Agreement:</strong> These Terms, Blue Water's Privacy Policy and any other agreement constitute the entire agreement between you and Blue Water.</li>
            <li><strong>Severability:</strong> If any provision is held invalid, the remainder shall continue in effect.</li>
            <li><strong>Assignment:</strong> You may not assign these Terms without Blue Water's written consent.</li>
            <li><strong>No Waiver:</strong> Our failure to enforce any right shall not be a waiver of future enforcement.</li>
          </ul>
        </section>

        {/* Section 13: Definitions */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            13. DEFINITIONS
          </h2>
          <div className="space-y-4">
            <div>
              <p className="font-medium mb-2" style={{ color: colors.primary }}><strong>Platform:</strong></p>
              <p className="pl-4" style={{ color: semanticColors.text.secondary }}>The software, website, and mobile application owned by Blue Water.</p>
            </div>
            <div>
              <p className="font-medium mb-2" style={{ color: colors.primary }}><strong>Provider:</strong></p>
              <p className="pl-4" style={{ color: semanticColors.text.secondary }}>An independent healthcare worker registered to offer shift services via the Platform.</p>
            </div>
            <div>
              <p className="font-medium mb-2" style={{ color: colors.primary }}><strong>Shift:</strong></p>
              <p className="pl-4" style={{ color: semanticColors.text.secondary }}>A scheduled engagement between a Facility and Provider for healthcare services.</p>
            </div>
            <div>
              <p className="font-medium mb-2" style={{ color: colors.primary }}><strong>User:</strong></p>
              <p className="pl-4" style={{ color: semanticColors.text.secondary }}>Any authorized person who accesses the Platform on behalf of a Facility.</p>
            </div>
            <div>
              <p className="font-medium mb-2" style={{ color: colors.primary }}><strong>Facility:</strong></p>
              <p className="pl-4" style={{ color: semanticColors.text.secondary }}>A licensed healthcare organization using the Platform to post shifts and engage Providers.</p>
            </div>
          </div>
        </section>

        {/* Section 14: Contact */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.primary }}>
            14. CONTACT
          </h2>
          <p className="mb-4" style={{ color: semanticColors.text.secondary }}>
            For questions or notices:
          </p>
          <div className="space-y-2" style={{ color: semanticColors.text.secondary }}>
            <p><strong>Vital Hive Technologies Support Team</strong></p>
            <p><strong>Email:</strong> support@vitalhivetechnologies.com</p>
            <p><strong>Address:</strong> 3950 Sunforest Ct. Suite 200, Toledo, Ohio 43623</p>
          </div>
        </section>

        {/* Contact */}
        <div className="mt-12 p-6 rounded-lg text-center" style={{ backgroundColor: semanticColors.background.secondary, border: `1px solid ${semanticColors.border.light}` }}>
          <p className="mb-2" style={{ color: semanticColors.text.secondary }}>
            Questions about these Terms of Service?
          </p>
          <a 
            href="mailto:support@vitalhivetechnologies.com"
            className="text-sm hover:underline transition-colors duration-200"
            style={{ color: colors.primary }}
          >
             support@vitalhivetechnologies.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
